import { Checkbox, Flex, Input, Modal, Select, Typography } from "antd";
import { useState } from "react";
import { CardBody, CardViewer } from "@/components/card-viewer";
import {
	AWS_SERVICE_CATALOG,
	CUSTOM_ACTION_PREFIX,
} from "./aws-service-catalog";

/** Sentinel value for the "Other" catch-all entry in the add-service dropdown. */
const OTHER_VALUE = "__other__";

/**
 * Controlled multi-select grid for granting the operator roles permissions by AWS service. Asked
 * once: each selected service contributes its read actions to the baseline Operations role and its
 * read + write actions to the Operations Max ceiling. Popular services show by default; a search
 * adds more from the catalog. Selection is owned by the parent (`value`/`onChange`).
 */
export function AwsPermissionsPicker({
	value,
	onChange,
	readOnly,
}: {
	/** Selected service keys. */
	value: string[];
	onChange: (keys: string[]) => void;
	/** Only read actions are granted — preview them alone and hide the custom-action escape hatch,
	 * which grants to the ceiling only and would be silently dropped here. */
	readOnly?: boolean;
}) {
	const selected = new Set(value);
	const [otherOpen, setOtherOpen] = useState(false);
	const [otherText, setOtherText] = useState("");

	// Show the popular services plus anything currently selected (e.g. added via search).
	const visible = AWS_SERVICE_CATALOG.filter(
		(s) => s.popular || selected.has(s.key),
	);
	const addable = AWS_SERVICE_CATALOG.filter(
		(s) => !s.popular && !selected.has(s.key),
	);
	// User-typed "Other" actions, stored inline in the value as `custom:<action>`.
	const customActions = value.filter((k) => k.startsWith(CUSTOM_ACTION_PREFIX));

	const toggle = (key: string) =>
		onChange(
			selected.has(key) ? value.filter((k) => k !== key) : [...value, key],
		);

	const addCustomActions = () => {
		const actions = otherText
			.split(/[\s,]+/)
			.map((a) => a.trim())
			.filter(Boolean)
			.map((a) => `${CUSTOM_ACTION_PREFIX}${a}`);
		onChange([...new Set([...value, ...actions])]);
		setOtherText("");
		setOtherOpen(false);
	};

	// Preview the ceiling's view — the wider of the two — so each card shows the full extent of
	// what selecting that service can ever unlock.
	const options = [
		...visible.map((svc) => ({
			key: svc.key,
			label: svc.label,
			actions: (readOnly ? svc.read : [...svc.read, ...svc.write]).join(", "),
		})),
		...customActions.map((k) => ({
			key: k,
			label: "Custom (ceiling only)",
			actions: k.slice(CUSTOM_ACTION_PREFIX.length),
		})),
	];

	return (
		<Flex vertical gap={12}>
			<CardViewer
				items={options}
				getKey={(o) => o.key}
				onOpen={(o) => toggle(o.key)}
				isSelected={(o) => selected.has(o.key)}
				minCardWidth={220}
				renderCard={(o) => (
					<CardBody
						title={o.label}
						leading={
							<Checkbox
								checked={selected.has(o.key)}
								// The card's own onClick already toggles selection.
								onClick={(e) => e.preventDefault()}
							/>
						}
						description={o.actions}
					/>
				)}
			/>

			<Select
				showSearch
				value={null}
				placeholder="Add another service…"
				style={{ maxWidth: 280 }}
				optionFilterProp="label"
				onChange={(key: string) => {
					if (key === OTHER_VALUE) setOtherOpen(true);
					else if (key) toggle(key);
				}}
				options={[
					...addable.map((s) => ({ value: s.key, label: s.label })),
					...(readOnly
						? []
						: [{ value: OTHER_VALUE, label: "Other (custom actions)…" }]),
				]}
			/>

			<Modal
				open={otherOpen}
				title="Add custom IAM actions"
				okText="Add"
				onOk={addCustomActions}
				onCancel={() => setOtherOpen(false)}
			>
				<Typography.Paragraph type="secondary">
					Enter IAM actions to grant, separated by spaces, commas, or new lines
					(e.g. <code>s3:GetObject</code>, <code>ec2:DescribeInstances</code>).
				</Typography.Paragraph>
				<Typography.Paragraph type="secondary">
					These go to the escalation ceiling only, never to the baseline role —
					so an agent can request them with approval, but never use them
					unattended.
				</Typography.Paragraph>
				<Input.TextArea
					rows={4}
					value={otherText}
					onChange={(e) => setOtherText(e.target.value)}
					placeholder="s3:GetObject, ec2:Describe*"
				/>
			</Modal>
		</Flex>
	);
}
